import {Dialog as HUIDialog, DialogBackdrop, DialogPanel} from '@headlessui/react'
import {createContext} from 'react'

import {noop, twMerge} from '@sdox/utils'

import {DialogContent} from './Dialog.Content'
import {DialogFooter} from './Dialog.Footer'
import {DialogHeading} from './Dialog.Heading'
import {DialogSource} from './Dialog.Source'
import {DialogDescription} from './Dialog.Description'
import {DialogHint} from './Dialog.Hint'
import {DialogStyledSection} from './Dialog.StyledSection'
import {useCloseDialogOnBrowserBack} from '../../hooks/useCloseDialogOnBrowserBack'

import type {ReactNode} from 'react'
import type {DialogProps as HUIDialogProps} from '@headlessui/react'

export type DialogProps = HUIDialogProps & {
  children: ReactNode
  className?: string
  heading?: ReactNode
  size?: 'small' | 'medium'
  persistent?: boolean
  limitHeight?: boolean
  fixHeight?: boolean
  closeOnBrowserBack?: boolean
}

type DialogContextState = {
  dialogHasHeading: boolean
  size: DialogProps['size']
}

export const DialogContext = createContext<DialogContextState>({dialogHasHeading: false, size: 'medium'})

export const Dialog = (props: DialogProps) => {
  const {
    children,
    className,
    heading,
    open,
    onClose,
    closeOnBrowserBack = true,
    persistent,
    size = 'medium',
    limitHeight = true,
    fixHeight = false,
    ...restProps
  } = props

  useCloseDialogOnBrowserBack({onClose, closeOnBrowserBack, open})

  return (
    <HUIDialog
      open={open}
      onClose={persistent ? noop : onClose}
      className={twMerge('fixed inset-0 z-[100] overflow-hidden', className)}
      {...restProps}
    >
      <DialogContext.Provider value={{dialogHasHeading: !!heading, size}}>
        <DialogBackdrop className={'absolute inset-0 z-10 bg-scrim-primary/15'}/>
        <div className={'absolute inset-0 z-10 w-screen overflow-y-auto px-spacing-16 py-spacing-32'}>
          <div className={'flex min-h-full items-center justify-center'}>
            <DialogPanel
              className={twMerge(
                size === 'small' && 'w-[560px]',
                size === 'small' && limitHeight && 'xs:max-h-dialog-sm',
                size === 'small' && fixHeight && 'xs:h-dialog-sm',
                size === 'medium' && 'w-[960px]',
                size === 'medium' && limitHeight && 'xs:max-h-dialog-md',
                size === 'medium' && fixHeight && 'h-dialog-md',
                fixHeight && 'h-dialog-full',
                'flex max-h-dialog-full max-w-full flex-col overflow-hidden rounded-radius-lg bg-surface-primary',
                'relative', // without relative matomo can not track impressions of items inside the dialog
              )}
            >
              {heading && (
                <DialogHeading
                  className={twMerge(
                    size === 'small' && 'p-spacing-32 pb-spacing-12',
                    size === 'medium' && 'p-spacing-32 pb-spacing-12 sm:p-spacing-40 sm:pb-spacing-16',
                  )}
                >
                  {heading}
                </DialogHeading>
              )}
              {children}
            </DialogPanel>
          </div>
        </div>
      </DialogContext.Provider>
    </HUIDialog>
  )
}

Dialog.Content = DialogContent
Dialog.Description = DialogDescription
Dialog.Footer = DialogFooter
Dialog.Heading = DialogHeading
Dialog.Hint = DialogHint
Dialog.Source = DialogSource
Dialog.StyledSection = DialogStyledSection
